/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import MENU_ROLES from '@/constants/ConstantMenuRoles';
import { useStoreAuth } from '@/stores/StoreAuth';
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router';

export let wasPageReloaded = false;

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      component: () => import('@/layouts/LayoutAuth.vue'),
      children: [
        {
          path: '',
          name: 'RouteLogin',
          component: () => import('@/pages/auth/PageLogin.vue'),
        },
        {
          path: 'token/tfa/confirm/:token?',
          name: 'RouteConfirmTokenTfa',
          props: true,
          component: () => import('@/pages/auth/PageTfaToken.vue'),
        },
        {
          path: 'forgot/email',
          name: 'RouteForgotEmail',
          component: () => import('@/pages/auth/PageForgotPasswordEmail.vue'),
        },
        {
          path: 'forgot/email/confirm/:token',
          name: 'RouteForgotEmailConfirm',
          props: true,
          component: () => import('@/pages/auth/PageForgotPasswordConfirm.vue'),
        },
        {
          path: 'registration/first/access/:id',
          name: 'RouteTfaFirstAccess',
          props: true,
          component: () => import('@/pages/auth/PageTfaFirstAccess.vue'),
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/layouts/LayoutMain.vue'),
      children: [
        {
          path: '',
          name: 'RouteHome',
          component: () => import('@/pages/PageHome.vue'),
        },
        {
          path: 'saude-e-seguranca',
          meta: {
            roles: MENU_ROLES.SAUDE_SEGURANCA_AGENTES_NOCIVOS_APOSENTADORIA_ESPECIAL,
          },
          children: [
            {
              path: 'agentes-nocivos-e-aposentadoria-especial',
              name: 'RouteSaudeSegurancaAgentesNocivosAposentadoriaEspecial',
              meta: {
                roles: MENU_ROLES.SAUDE_SEGURANCA_AGENTES_NOCIVOS_APOSENTADORIA_ESPECIAL,
              },
              component: () =>
                import('@/pages/saude-e-seguranca/PageSaudeSegurancaAgentesNocivosAposentadoriaEspecial.vue'),
            },
          ],
        },
        {
          path: 'configuracoes',
          children: [
            {
              path: 'encargos',
              children: [
                {
                  path: 'integracao',
                  name: 'RouteSettingsEcacIntegration',
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/fonte-dados/PageFonteDadosEcacProcess.vue'),
                },
                {
                  path: 'mensagens-ecac',
                  name: 'RouteConfiguracaoMensagensEcac',
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/PageManagementMensagensEcac.vue'),
                },
              ],
            },
            {
              path: 'usuarios',
              name: 'RouteSettingsUsers',
              meta: {
                roles: MENU_ROLES.ADMIN,
              },
              component: () => import('@/pages/management/PageManagementUsuarios.vue'),
            },
            {
              path: 'usuarios/:id',
              name: 'RouteSettingsUser',
              props: true,
              redirect: { name: 'RouteSettingsUserData' },
              component: () => import('@/pages/management/PageManagementUsuario.vue'),
              children: [
                {
                  path: 'dados',
                  name: 'RouteSettingsUserData',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioDados.vue'),
                },
                {
                  path: 'permissoes',
                  name: 'RouteSettingsUserPermissions',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioPermissoes.vue'),
                },
                {
                  path: 'redefinir-senha',
                  name: 'RouteSettingsUserSecurity',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioSeguranca.vue'),
                },
                {
                  path: 'autenticacao-2fa',
                  name: 'RouteSettingsUserAuthentication',
                  props: true,
                  meta: {
                    middleware: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioAuthentication.vue'),
                },
                {
                  path: 'editor-de-menu',
                  name: 'RouteSettingsUserEditorMenu',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioEditorTelas.vue'),
                },
                {
                  path: 'estabelecimentos',
                  name: 'RouteSettingsUserEstablishments',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/usuario/PageManagementUsuarioEstabelecimentos.vue'),
                },
              ],
            },
            {
              path: 'empresas',
              children: [
                {
                  path: '',
                  name: 'RouteSettingsCompanies',
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/PageEmpresas.vue'),
                },
                {
                  path: 'estabelecimentos',
                  name: 'RouteSettingsEstablishments',
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/PageManagementEstabelecimentos.vue'),
                },
                {
                  path: 'grupos-economicos',
                  name: 'RouteSettingsGroups',
                  meta: {
                    roles: MENU_ROLES.ADMIN,
                  },
                  component: () => import('@/pages/management/PageManagementGrupos.vue'),
                },
              ],
            },
            {
              path: 'e-social',
              children: [
                {
                  path: 'importacao',
                  name: 'RouteSettingsImport',
                  meta: {
                    roles: MENU_ROLES.IMPORTACAO,
                  },
                  component: () => import('@/pages/fonte-dados/PageFonteDadosUploadArquivoZip.vue'),
                },
                {
                  path: 'tabelas',
                  children: [
                    {
                      path: '',
                      name: 'RouteSettingsESocialTables',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialTables.vue'),
                    },
                    {
                      path: 'natureza-das-rubricas',
                      name: 'RouteSettingsESocialTablesRubricNature',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialRubricNature.vue'),
                    },
                    {
                      path: 'tipos-de-rubrica',
                      name: 'RouteSettingsESocialTablesRubricType',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialRubricType.vue'),
                    },
                    {
                      path: 'incidencias-para-a-cp',
                      name: 'RouteSettingsESocialTablesIncidenceINSS',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialIncidenceCP.vue'),
                    },
                    {
                      path: 'incidencias-para-o-irrf',
                      name: 'RouteSettingsESocialTablesIncidenceIRRF',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialIncidenceIRRF.vue'),
                    },
                    {
                      path: 'incidencias-para-o-fgts',
                      name: 'RouteSettingsESocialTablesIncidenceFGTS',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialIncidenceFGTS.vue'),
                    },
                    {
                      path: 'incidencias-para-a-cprp',
                      name: 'RouteSettingsESocialTablesIncidenceCPRP',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageManagementESocialIncidenceCPRP.vue'),
                    },
                    {
                      path: 'incidencias-para-o-pis-pasep',
                      name: 'RouteSettingsESocialTablesIncidencePISPASEP',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () =>
                        import('@/pages/management/e-social/PageManagementESocialIncidenciaPISPASEP.vue'),
                    },
                    {
                      path: 'cbo',
                      name: 'RouteConfiguracoesESocialTabelasCBO',
                      meta: {
                        roles: MENU_ROLES.ADMIN,
                      },
                      component: () => import('@/pages/management/e-social/PageConfiguracoesESocialTabelasCBO.vue'),
                    },
                  ],
                },
                {
                  path: 'parametros-de-analise/incidencias-por-natureza',
                  name: 'RouteSettingsESocialAnalysisParametersIncidenceNature',
                  meta: {
                    roles: MENU_ROLES.CONFIG_RUBRICAS_INCIDENCIAS_NATUREZA,
                  },
                  component: () => import('@/pages/management/e-social/PageManagementESocialIncidenceNature.vue'),
                },
                {
                  path: 'parametros-de-analise/rubricas/classificacao-de-analise',
                  name: 'RotaConfiguracoesESocialParametrosAnaliseRubricasClassificacaoAnalise',
                  meta: {
                    roles: MENU_ROLES.CONFIG_RUBRICAS_CLASSIFICACAO_ANALISE,
                  },
                  component: () =>
                    import('@/pages/management/e-social/PageManagementESocialRubricasClassificacaoAnalise.vue'),
                },
                {
                  path: 'parametros-de-analise/cbo/classificacao-de-analise',
                  name: 'RotaConfiguracoesESocialParametrosAnaliseCBOClassificacaoAnalise',
                  meta: {
                    roles: MENU_ROLES.CONFIG_CBO_CLASSIFICACAO_ANALISE,
                  },
                  component: () =>
                    import('@/pages/management/e-social/PageManagementESocialCboClassificacaoRisco.vue'),
                },
                {
                  path: 'parametros-de-analise/horarios-e-jornada/classificacao-de-analise',
                  name: 'RotaConfiguracoesESocialParametrosAnaliseHorariosJornadaClassificacaoAnalise',
                  meta: {
                    roles: MENU_ROLES.CONFIG_HORARIOS_JORNADA_CLASSIFICACAO_ANALISE,
                  },
                  component: () => import('@/pages/management/e-social/PageManagementESocialHorariosJornada.vue'),
                },
              ],
            },
            {
              path: 'auditoria',
              name: 'RouteSettingsAudit',
              meta: {
                roles: MENU_ROLES.ADMIN,
              },
              component: () => import('@/pages/management/PageManagementAuditoria.vue'),
            },
            {
              path: 'cadastro-de-documentos',
              name: 'RouteSettingsRegistrationDocuments',
              meta: {
                roles: MENU_ROLES.ADMIN,
              },
              component: () => import('@/pages/documents/files/PageFileUpload.vue'),
            },
          ],
        },
        {
          path: 'eGov',
          children: [
            {
              path: 'cessao',
              name: 'RouteTrabalhadorCessao',
              meta: {
                roles: MENU_ROLES.CESSAO,
              },
              component: () => import('@/pages/eGOV/cessao/PageTrabalhadorCessao.vue'),
            },
          ],
        },
        {
          path: 'folha-de-pagamento',
          children: [
            {
              path: 'rubricas-nova/:tab?',
              children: [
                {
                  path: '',
                  name: 'RouteFolhaPagamentoRubricas',
                  meta: {
                    roles: MENU_ROLES.FOLHA_DE_PAGAMENTO,
                    temNavegadorPerfil: true,
                  },
                  component: () => import('@/pages/folha-de-pagamento/rubricas/PageFolhaPagamentoRubricas.vue'),
                },
                {
                  path: 'rubricas-nova/detalhe/:idRubrica',
                  name: 'RouteFolhaPagamentoRubricasDetalhe',
                  meta: {
                    roles: MENU_ROLES.FOLHA_DE_PAGAMENTO,
                    temNavegadorPerfil: true,
                  },
                  component: () => import('@/pages/folha-de-pagamento/rubricas/PageFolhaPagamentoRubricasDetalhe.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'trabalhador',
          children: [
            {
              path: 'afastamento-e-inatividade',
              name: 'RouteTrabalhadorAfastamentoInatividade',
              meta: {
                roles: MENU_ROLES.AFASTADOS_E_INATIVOS,
              },
              component: () =>
                import('@/pages/trabalhador/afastamento-e-inatividade/PageTrabalhadorAfastamentoEInatividade.vue'),
            },
            {
              path: 'cbo/:tab?',
              children: [
                {
                  path: '',
                  name: 'RouteTrabalhadorCBO',
                  meta: {
                    roles: MENU_ROLES.CBO,
                    temNavegadorPerfil: true,
                  },
                  component: () => import('@/pages/trabalhador/cbo/PageTrabalhadorCBO.vue'),
                },
                {
                  path: 'detalhe/:idTrabalhador/:tabDetalhe',
                  name: 'RouteTrabalhadorCBODetalhes',
                  meta: {
                    roles: MENU_ROLES.CBO,
                    temNavegadorPerfil: true,
                  },
                  component: () => import('@/pages/trabalhador/cbo/PageTrabalhadorCBODetalhes.vue'),
                },
              ],
            },
            {
              path: 'horarios-jornada',
              name: 'RouteTrabalhadorHorariosJornadas',
              meta: {
                roles: MENU_ROLES.HORARIOS_E_JORNADA,
                temNavegadorPerfil: true,
              },
              component: () => import('@/pages/trabalhador/horarios-jornada/PageTrabalhadorHorariosJornada.vue'),
            },
            {
              path: 'horarios-jornada/detalhes/:idTrabalhador/:tab',
              name: 'RouteTrabalhadorHorariosJornadaDetalhes',
              meta: {
                roles: MENU_ROLES.HORARIOS_E_JORNADA,
                temNavegadorPerfil: true,
              },
              component: () =>
                import('@/pages/trabalhador/horarios-jornada/PageTrabalhadorHorariosJornadaDetalhes.vue'),
            },
          ],
        },
        {
          path: 'encargos',
          children: [
            {
              path: 'dctf',
              name: 'RouteEncargosDctf',
              meta: {
                roles: MENU_ROLES.DCTF,
              },
              component: () => import('@/pages/encargos/PageEncargosDCTF.vue'),
            },
            {
              path: 'oportunidades',
              name: 'RouteEncargosOportunidades',
              meta: {
                roles: MENU_ROLES.ENCARGOS_OPORTUNIDADES,
              },
              component: () => import('@/pages/encargos/PageEncargosOportunidades.vue'),
            },
            {
              path: 'mensagens-ecac',
              name: 'RouteMensagensEcac',
              props: true,
              meta: {
                roles: MENU_ROLES.MENSAGENS_ECAC,
              },
              component: () => import('@/pages/encargos/PageEncargosMensagensEcac.vue'),
            },
          ],
        },
        {
          path: 'ativacao',
          name: 'RouteRegistrationUserActivation',
          props: true,
          meta: {
            roles: MENU_ROLES.ADMIN,
          },
          component: () => import('@/pages/management/usuario/PageManagementUsuarioCadastradoAtivacao.vue'),
        },
        {
          path: 'central-de-ajuda',
          name: 'RouteCentralAjuda',
          component: () => import('@/pages/help-desk/PageCentralAjuda.vue'),
        },
        {
          path: 'minha-conta',
          name: 'RouteLayoutProfile',
          redirect: { name: 'RouteLayoutProfileEdit' },
          component: () => import('@/layouts/LayoutUserProfile.vue'),
          children: [
            {
              path: 'perfil',
              name: 'RouteLayoutProfileEdit',
              component: () => import('@/pages/auth/PagePerfil.vue'),
            },
            {
              path: 'editar-senha',
              name: 'RouteLayoutProfilePasswordChange',
              component: () => import('@/pages/auth/PageChangePassword.vue'),
            },
            {
              path: 'autenticacao-2fa',
              name: 'RouteLayoutProfileAuthentication',
              component: () => import('@/pages/auth/PageAuthentication2FA.vue'),
            },
          ],
        },
        {
          path: 'certidoes',
          children: [
            {
              path: 'certidao-federal',
              name: 'RouteCertificatesCnd',
              meta: {
                roles: MENU_ROLES.CERTIDAO_FEDERAL,
              },
              component: () => import('@/pages/certidoes/certidao-federal/PageCertidoesCertidaoFederal.vue'),
            },
            {
              path: 'historico/:idCertidao',
              name: 'RouteCertificatesHistoric',
              props: true,
              meta: {
                roles: MENU_ROLES.CERTIDAO_FEDERAL,
              },
              component: () => import('@/pages/certidoes/certidao-federal/PageCertidoesCertidaoFederalHistorico.vue'),
            },
            {
              path: 'crf',
              children: [
                {
                  path: '',
                  name: 'RouteCrf',
                  meta: {
                    roles: MENU_ROLES.CRF,
                  },
                  component: () => import('@/pages/certidoes/crf/PageCertidoesCRF.vue'),
                },
                {
                  path: 'historico/:idCrf',
                  name: 'RouteCrfHistoric',
                  props: true,
                  meta: {
                    roles: MENU_ROLES.CRF,
                  },
                  component: () => import('@/pages/certidoes/crf/PageCertidoesCRFHistorico.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'anexos',
          name: 'RouteDocumentsFile',
          component: () => import('@/pages/documents/files/PageFile.vue'),
        },
        {
          path: 'exportacoes',
          name: 'RouteDocumentsDownload',
          component: () => import('@/pages/documents/downloads/PageDownload.vue'),
        },
        {
          path: 'central-de-reprocessamento',
          meta: { roles: MENU_ROLES.ADMIN },
          children: [
            {
              path: '',
              name: 'RouteCentralReprocessamento',
              meta: {
                roles: MENU_ROLES.CENTRAL_REPROCESSAMENTO,
              },
              component: () => import('@/pages/central-reprocessamento/PageCentralReprocessamento.vue'),
            },
            {
              path: ':id',
              name: 'RouteCentralReprocessamentoDetalhes',
              meta: {
                roles: MENU_ROLES.CENTRAL_REPROCESSAMENTO,
              },
              component: () => import('@/pages/central-reprocessamento/PageCentralReprocessamentoDetalhes.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/playground',
      component: () => import('@/layouts/LayoutPlayground.vue'),
      children: [
        {
          path: '',
          name: 'StableHome',
          component: () => import('@/pages/playground/PagePlaygroundHome.vue'),
        },
        {
          path: 'truncate',
          name: 'PlaygroundTruncatedText',
          meta: { title: 'Truncated Text', subtitle: 'AGTruncatedText' },
          component: () => import('@/pages/playground/PagePlaygroundTruncatedText.vue'),
        },
        {
          path: 'Label',
          name: 'PlaygroundLabel',
          meta: { title: 'Labels', subtitle: 'AGLabel' },
          component: () => import('@/pages/playground/PagesPlaygroundLabel.vue'),
        },
        {
          path: 'button',
          name: 'PlaygroundButton',
          meta: { title: 'Buttons', subtitle: 'AGButton' },
          component: () => import('@/pages/playground/PagePlaygroundButtons.vue'),
        },
        {
          path: 'button-icon',
          name: 'PlaygroundButtonIcon',
          meta: { title: 'Icon Buttons', subtitle: 'AGButtonIcon' },
          component: () => import('@/pages/playground/PagePlaygroundButtonsIcon.vue'),
        },
        {
          path: 'expansion-panel',
          name: 'PlaygroundExpansionPanel',
          meta: { title: 'Expansion Panel', subtitle: 'AGExpansionPanel' },
          component: () => import('@/pages/playground/PagePlaygroundExpansionPanel.vue'),
        },
        {
          path: 'risco-legenda',
          name: 'PlaygroundRiscoLegenda',
          meta: { title: 'Risco Legenda', subtitle: 'AGMatrizExposicaoLegenda' },
          component: () => import('@/pages/playground/PagePlaygroundRiscoLegenda.vue'),
        },
        {
          path: 'table-server',
          name: 'PlaygroundTableServer',
          meta: { title: 'Table Server', subtitle: 'AGDataTableServer' },
          component: () => import('@/pages/playground/PagePlaygroundTableServer.vue'),
        },
        {
          path: 'table',
          name: 'PlaygroundTable',
          meta: { title: 'Table', subtitle: 'AGDataTable' },
          component: () => import('@/pages/playground/PagePlaygroundTable.vue'),
        },
        {
          path: 'tabs',
          name: 'PlaygroundTabs',
          meta: { title: 'Tabs', subtitle: 'AGTabs' },
          component: () => import('@/pages/playground/PagePlaygroundTabs.vue'),
        },
        {
          path: 'tags',
          name: 'PlaygroundTags',
          meta: { title: 'Tags', subtitle: 'AGTag' },
          component: () => import('@/pages/playground/PagePlaygroundTags.vue'),
        },
        {
          path: 'switch',
          name: 'PlaygroundSwitch',
          meta: { title: 'Switches', subtitle: 'AGSwitch' },
          component: () => import('@/pages/playground/PagePlaygroundSwitch.vue'),
        },
        {
          path: 'menu',
          name: 'PlaygroundMenu',
          meta: { title: 'Menus', subtitle: 'AGMenu' },
          component: () => import('@/pages/playground/PagePlaygroundMenu.vue'),
        },
        {
          path: 'text-field',
          name: 'PlaygroundTextField',
          meta: { title: 'Text fields', subtitle: 'AGTextField' },
          component: () => import('@/pages/playground/PagePlaygroundTextField.vue'),
        },
        {
          path: 'text-area',
          name: 'PlaygroundTextArea',
          meta: { title: 'Text area', subtitle: 'AGTextArea' },
          component: () => import('@/pages/playground/PagePlaygroundTextArea.vue'),
        },
        {
          path: 'currency-input',
          name: 'PlaygroundCurrencyInput',
          meta: { title: 'Currency Inputs', subtitle: 'AGCurrencyInput' },
          component: () => import('@/pages/playground/PagePlaygroundCurrencyInput.vue'),
        },
        {
          path: 'autocomplete',
          name: 'PlaygroundAutocomplete',
          meta: { title: 'Autocompletes', subtitle: 'AGAutocomplete' },
          component: () => import('@/pages/playground/PagePlaygroundAutocomplete.vue'),
        },
        {
          path: 'ag-input-field',
          name: 'PlaygroundAGInputField',
          meta: { title: 'AGInputField', subtitle: 'AGInputField' },
          component: () => import('@/pages/playground/PagePlaygroundAGInputField.vue'),
        },
        {
          path: 'filter',
          name: 'PlaygroundFilter',
          meta: { title: 'Filters', subtitle: 'AGFilter' },
          component: () => import('@/pages/playground/PagePlaygroundFilter.vue'),
        },
        {
          path: 'bar-graph',
          name: 'PlaygroundBarGraph',
          meta: { title: 'Gráfico de barras', subtitle: 'AGGraficoBarra' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoBarra.vue'),
        },
        {
          path: 'icon',
          name: 'PlaygroundIcon',
          meta: { title: 'Icons', subtitle: 'AGIcon' },
          component: () => import('@/pages/playground/PagePlaygroundIcon.vue'),
        },
        {
          path: 'dialog',
          name: 'PlaygroundDialog',
          meta: { title: 'Dialogs', subtitle: 'AGDialog' },
          component: () => import('@/pages/playground/PagePlaygroundDialog.vue'),
        },
        {
          path: 'notification',
          name: 'PlaygroundNotification',
          meta: { title: 'Notifications', subtitle: 'AGNotification' },
          component: () => import('@/pages/playground/PagePlaygroundNotification.vue'),
        },
        {
          path: 'confirm',
          name: 'PlaygroundConfirm',
          meta: { title: 'Confirmações', subtitle: 'AGDialogConfirm' },
          component: () => import('@/pages/playground/PagePlaygroundConfirm.vue'),
        },
        {
          path: 'alert',
          name: 'PlaygroundAlert',
          meta: { title: 'Alerts', subtitle: 'AGAlert' },
          component: () => import('@/pages/playground/PagePlaygroundAlert.vue'),
        },
        {
          path: 'password',
          name: 'PlaygroundPassword',
          meta: { title: 'Password Field', subtitle: 'AGPasswordField' },
          component: () => import('@/pages/playground/PagePlaygroundPasswordField.vue'),
        },
        {
          path: 'input-file',
          name: 'PlaygroundInputFile',
          meta: { title: 'Input File', subtitle: 'AGInputFile' },
          component: () => import('@/pages/playground/PagePlaygroundInputFile.vue'),
        },
        {
          path: 'date-picker',
          name: 'PlaygroundDatePicker',
          meta: { title: 'Date Picker', subtitle: 'AGDatePicker' },
          component: () => import('@/pages/playground/PagePlaygroundDatePicker.vue'),
        },
        {
          path: 'date-picker-month',
          name: 'PlaygroundDatePickerMonth',
          meta: { title: 'Month Picker', subtitle: 'AGDatePickerMonth' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerMonth.vue'),
        },
        {
          path: 'select',
          name: 'PlaygroundSelect',
          meta: { title: 'Select', subtitle: 'AGSelect' },
          component: () => import('@/pages/playground/PagePlaygroundSelect.vue'),
        },
        {
          path: 'date-picker-range',
          name: 'PlaygroundDatePickerRange',
          meta: { title: 'Date Picker Range', subtitle: 'AGDatePickerRange' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerRange.vue'),
        },
        {
          path: 'month-picker-range',
          name: 'PlaygroundDatePickerRangeMonth',
          meta: { title: 'Month Picker Range', subtitle: 'AGDatePickerRangeMonth' },
          component: () => import('@/pages/playground/PagePlaygroundDatePickerRangeMonth.vue'),
        },
        {
          path: 'card',
          name: 'PlaygroundCard',
          meta: { title: 'Card', subtitle: 'AGCard' },
          component: () => import('@/pages/playground/PagePlaygroundCard.vue'),
        },
        {
          path: 'risco-matriz',
          name: 'PlaygroundRiscoMatriz',
          meta: { title: 'Matriz de Risco', subtitle: 'AGMatrizExposicaoTabela' },
          component: () => import('@/pages/playground/PagePlaygroundRiscoMatriz.vue'),
        },
        {
          path: 'message-color',
          name: 'RoutePlaygroundMessageColor',
          meta: { title: 'Message Color', subtitle: 'Estilizando os hints dos componentes' },
          component: () => import('@/pages/playground/PagePlaygroundMessageColor.vue'),
        },
        {
          path: 'radio-button',
          name: 'RoutePlaygroundRadioButton',
          meta: { title: 'Radio Button', subtitle: 'AGRadioButton' },
          component: () => import('@/pages/playground/PagePlaygroundRadioButton.vue'),
        },
        {
          path: 'grafico-linha',
          name: 'RoutePlaygroundGraficoLinha',
          meta: { title: 'Gráfico de linha', subtitle: 'AGGraficoLinha' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoLinha.vue'),
        },
        {
          path: 'indicador-rosca',
          name: 'RoutePlaygroundIndicadorRosca',
          meta: { title: 'Indicador Rosca', subtitle: 'AGIndicadorRosca' },
          component: () => import('@/pages/playground/PagePlaygroundIndicadorRosca.vue'),
        },
        {
          path: 'indicador-linha',
          name: 'RoutePlaygroundIndicadorLinha',
          meta: { title: 'Indicador Linha', subtitle: 'AGIndicadorLinha' },
          component: () => import('@/pages/playground/PagePlaygroundIndicadorLinha.vue'),
        },
        {
          path: 'grafico-barra-percentual',
          name: 'RoutePlaygroundGraficoBarraPercentual',
          meta: { title: 'Gráfico de barra com porcentagem', subtitle: 'AGGraficoBarraPercentual' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoBarraPercentual.vue'),
        },
        {
          path: 'grafico-rosca',
          name: 'RoutePlaygroundGraficoRosca',
          meta: { title: 'Gráfico de rosca', subtitle: 'AGGraficoRosca' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoRosca.vue'),
        },
        {
          path: 'grafico-coluna',
          name: 'RoutePlaygroundGraficoColuna',
          meta: { title: 'Gráfico de coluna', subtitle: 'AGGraficoColuna' },
          component: () => import('@/pages/playground/PagePlaygroundGraficoColuna.vue'),
        },
        {
          path: 'tooltip',
          name: 'PlaygroundTooltip',
          meta: { title: 'Tooltip', subtitle: 'AGTooltip' },
          component: () => import('@/pages/playground/PagePlaygroundTooltip.vue'),
        },
        {
          path: 'drop-zone',
          name: 'RoutePlaygroundDropzone',
          meta: { title: 'Drop zone', subtitle: 'AGDropzone' },
          component: () => import('@/pages/playground/PagePlaygroundDropzone.vue'),
        },
      ],
    },
    {
      path: '/erro',
      component: () => import('@/layouts/LayoutErro.vue'),
      children: [
        {
          path: '404',
          name: 'RouteErro404',
          component: () => import('@/pages/erros/PageErro404.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'RouteErro404' },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (!to.meta?.roles) return next();

  const storeAuth = useStoreAuth();

  if (!storeAuth.user?.id) {
    await storeAuth.getUser();
  }

  const hasPermission = to.meta.roles.some((role) => storeAuth.authorities?.includes(role));
  storeAuth.setMessage('');

  if (!storeAuth.token) {
    return next({ name: 'RouteLogin' });
  }

  if (!hasPermission) {
    storeAuth.setMessage(
      'Esta funcionalidade não está disponível no seu plano de serviço. Solicite este modulo para seu consultor da AGNES.',
    );

    return next({ name: 'RouteHome' });
  }

  wasPageReloaded = from === START_LOCATION;

  return next();
});

export default router;
